import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { LoadingButton } from '@mui/lab';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
// formik validation
import { Form, FormikProvider, useFormik } from 'formik';
// @mui
import { Container, Typography, Divider, Button, Box, Dialog, DialogTitle, DialogActions } from '@mui/material';
import { toast } from 'react-toastify';
// components
import AccountSettingsForm from '../sections/auth/accountSettings/AccountSettingsForm';
import Iconify from '../components/iconify/Iconify';
import { getProfile, updateProfile } from '../sections/auth/accountSettings/AccountAction';
import { deleteAccount } from '../services/deleteAccount';
import { commonLogoutFunc } from '../utils/share';
// ----------------------------------------------------------------------

export default function AccountSettingsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const accountData = useSelector((state) => state?.account || null);
  const [openDialog, setOpenDialog] = useState(false);

  const defaultFormData = useRef({
    userName: '',
    email: '',
    userTimezone: '',
    dailyReportEmail: true,
    dailyStatusChangeEmail: true,
  });

  const handleDialogOpen = () => {
    setOpenDialog(true);
  };
  const handleDialogClose = () => setOpenDialog(false);

  const handleDelete = async () => {
    try {
      await deleteAccount();
      commonLogoutFunc();
      navigate('/auth/login');
      toast.success('Successfully deleted a your account');
      handleDialogClose();
      // Delete Function
    } catch (error) {
      toast.error('Error while deleting');
      console.error(error);
      // handle error
    }
  };

  // Schema for validation
  const ProfileUpdateSchema = Yup.object().shape({
    userName: Yup.string().min(2, 'Too Short!').required('User name required'),
    userTimezone: Yup.string().required('Time zone required'),
    dailyReportEmail: Yup.boolean(),
    dailyStatusChangeEmail: Yup.boolean(),
  });
  const formik = useFormik({
    initialValues: defaultFormData.current,
    validationSchema: ProfileUpdateSchema,

    onSubmit: (values) => {
      const body = {
        userName: values.userName,
        userTimezone: values.userTimezone,
        dailyReportEmail: values.dailyReportEmail,
        dailyStatusChangeEmail: values.dailyStatusChangeEmail,
      };
      dispatch(updateProfile(body))
        .then(({ value }) => {
          if (value.data.status === 'success') {
            dispatch(getProfile()); // Refetch the profile data
            navigate('/dashboard');
            toast.success('Profile updated');
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  const { setSubmitting, handleSubmit, setValues, isSubmitting } = formik;

  useEffect(() => {
    dispatch(getProfile());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (accountData?.data) {
      delete accountData?.data?.password;
      defaultFormData.current = accountData?.data;
      setValues(accountData?.data);
    }
    // eslint-disable-next-line
  }, [accountData?.data]);

  return (
    <>
      <Helmet>
        <title> Account settings </title>
      </Helmet>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      <Divider />
      <FormikProvider value={formik}>
        <Form onSubmit={handleSubmit}>
          <Container maxWidth="sm">
            <Typography variant="h5" gutterBottom sx={{ my: 2 }}>
              Account settings
            </Typography>
            <AccountSettingsForm formik={formik} />
          </Container>
          <Divider sx={{ mt: 3 }} />
          <LoadingButton
            style={{ marginLeft: '40%', marginTop: '5%' }}
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
          >
            Save settings
          </LoadingButton>
        </Form>
      </FormikProvider>
      <Box sx={{ textAlign: 'right', mt: '1rem' }}>
        <Button color="error" startIcon={<Iconify icon={'gridicons:trash'} />} onClick={handleDialogOpen}>
          Delete Account
        </Button>
      </Box>
      <Dialog open={openDialog} onClose={handleDialogClose} aria-labelledby="delete-dialog-title">
        <DialogTitle id="delete-dialog-title">{'Are you sure you want to delete your account?'}</DialogTitle>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
